// TabBar.tsx — bottom navigation. Frosted, safe-area aware, touch-friendly.
import { Icon, type IconName } from './icons';
import { TRIAGE_V2 } from '@/config/features';

export type TabId = 'today' | 'journey' | 'toolkit' | 'library' | 'yourstuff';

export const TABS: { id: TabId; label: string; icon: IconName }[] = [
  { id: 'today', label: 'Today', icon: 'today' },
  { id: 'journey', label: 'Journey', icon: 'compass' },
  { id: 'library', label: 'Library', icon: 'book' },
  { id: 'yourstuff', label: 'Your stuff', icon: 'bookmark' },
];

// TRIAGE_V2: Journey is replaced by Toolkit (Journey stays reachable via the map links).
export const TRIAGE_TABS: { id: TabId; label: string; icon: IconName }[] = [
  { id: 'today', label: 'Today', icon: 'today' },
  { id: 'toolkit', label: 'Toolkit', icon: 'tool' },
  { id: 'library', label: 'Library', icon: 'book' },
  { id: 'yourstuff', label: 'Your stuff', icon: 'bookmark' },
];

export const tabsFor = (triage: boolean) => (triage ? TRIAGE_TABS : TABS);
export const ACTIVE_TABS = tabsFor(TRIAGE_V2);

/** TRIAL_FULL_LOCK: while locked, every tab except Today needs membership. */
export const isTabLocked = (id: TabId, locked: boolean) => locked && id !== 'today';
export const tabLabel = (label: string, locked: boolean) => (locked ? `${label}, locked, for members` : label);

export function TabBar({ tab, onTab, locked = false, onLocked }: { tab: TabId; onTab: (id: TabId) => void; locked?: boolean; onLocked?: () => void }) {
  return (
    <nav
      aria-label="Primary"
      style={{
        // Sticky (in-flow) so it lives inside the single scroll surface and pins to the
        // bottom while content scrolls behind its frosted blur. flexShrink:0 keeps its
        // full height in the shell's flex column. This is what lets a swipe that lands on
        // the tab bar still scroll the content (it shares the one scroller).
        position: 'sticky',
        bottom: 0,
        flexShrink: 0,
        zIndex: 40,
        // Lift above the home indicator using the safe-area inset.
        paddingBottom: 'calc(12px + var(--safe-bottom))',
        paddingTop: 11,
        paddingLeft: 'var(--safe-left)',
        paddingRight: 'var(--safe-right)',
        display: 'flex',
        justifyContent: 'space-around',
        background: 'var(--tabbar)',
        backdropFilter: 'blur(24px) saturate(150%)',
        WebkitBackdropFilter: 'blur(24px) saturate(150%)',
        borderTop: '1px solid var(--hair)',
        boxShadow:
          '0 -0.5px 0 rgba(255,255,255,0.6) inset, 0 -12px 30px -18px rgba(74,52,24,0.22)',
      }}
    >
      {ACTIVE_TABS.map((t) => {
        const on = tab === t.id;
        const lockedTab = isTabLocked(t.id, locked);
        return (
          <button
            key={t.id}
            onClick={() => (lockedTab ? onLocked?.() : onTab(t.id))}
            aria-current={on ? 'page' : undefined}
            aria-label={tabLabel(t.label, lockedTab)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px 10px',
              minHeight: 44,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 4,
              color: on ? 'var(--amber-deep)' : 'var(--ink-soft)',
              opacity: lockedTab ? 0.5 : on ? 1 : 0.62,
              filter: lockedTab ? 'saturate(0.3)' : undefined,
              flex: 1,
              position: 'relative',
            }}
          >
            <Icon name={t.icon} size={23} sw={on ? 1.9 : 1.6} />
            {lockedTab && (
              <span aria-hidden style={{ position: 'absolute', top: 0, left: 'calc(50% + 4px)', display: 'flex' }}>
                <Icon name="lock" size={12} sw={2} />
              </span>
            )}
            <span
              style={{
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: 10.5,
                fontWeight: on ? 600 : 500,
                letterSpacing: '0.01em',
              }}
            >
              {t.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
